/**
 * Case-owned Control Evaluation presentation.
 * Descriptive language only. No causal claims. No client-side usable-hours math.
 */

export const CONTROL_EVALUATION_RESULTS = ['lower', 'unchanged', 'higher', 'not_comparable'] as const;

export type ControlEvaluationCustomerResult =
  | 'lower'
  | 'unchanged'
  | 'higher'
  | 'not_comparable'
  | 'inconclusive';

export type ControlEvaluationSide = {
  report_id?: string | null;
  scan_id?: string | null;
  report_revision?: string | null;
  window_start_utc?: string | null;
  window_end_utc?: string | null;
  confirmed?: number | null;
  usable_hours?: number | null;
  rate?: number | null;
  relation?: string | null;
  source_type?: string | null;
};

export type ControlEvaluation = {
  evaluation_id?: string;
  case_id?: string;
  control?: {
    control_revision_id?: string | null;
    control_id?: string | null;
    revision?: number | null;
    implemented_at?: string | null;
    description?: string | null;
    t_source?: string | null;
  };
  before?: ControlEvaluationSide | null;
  after?: ControlEvaluationSide | null;
  descriptive_result?: string | null;
  customer_result?: ControlEvaluationCustomerResult | string | null;
  customer_result_label?: string | null;
  customer_copy?: string | null;
  comparability_verdict?: string | null;
  comparability_reason_codes?: string[];
  limitations?: string[];
  claim_percentage_allowed?: boolean;
  percent_change?: number | null;
  verification_assessment_id?: string | null;
};

export type ControlEvaluationsResponse = {
  ok?: boolean;
  safety_case_id?: string;
  control_evaluations?: ControlEvaluation[];
  control_evaluation?: ControlEvaluation | null;
  missing?: boolean;
  missing_reason_codes?: string[];
  missing_reasons?: string[];
  horizon_picker?: boolean;
  replayed?: boolean;
};

const CAUSAL_TOKENS = ['caused', 'prevented', 'reduced risk', 'improvement'];

export function formatUsableHours(value: unknown): string {
  if (value == null || value === '') return 'Not proven';
  const number = Number(value);
  if (!Number.isFinite(number)) return 'Not proven';
  return `${number.toFixed(number >= 10 ? 1 : 2)} usable hours`;
}

export function formatConfirmed(value: unknown): string {
  if (value == null || value === '') return 'Not available';
  const number = Number(value);
  if (!Number.isFinite(number)) return 'Not available';
  return `${number} confirmed`;
}

export function formatRate(value: unknown): string {
  if (value == null || value === '') return 'Rate withheld';
  const number = Number(value);
  if (!Number.isFinite(number)) return 'Rate withheld';
  return `${number.toFixed(3)}/usable hr`;
}

export function formatWindow(side?: ControlEvaluationSide | null): string {
  const start = String(side?.window_start_utc || '').trim();
  const end = String(side?.window_end_utc || '').trim();
  if (start && end) return `${start} → ${end}`;
  return String(side?.scan_id || side?.report_id || 'Source not bound');
}

export function isDescriptiveCopy(text: string): boolean {
  const lower = text.toLowerCase();
  return !CAUSAL_TOKENS.some((token) => lower.includes(token));
}

export function resultTestId(result: unknown): string {
  const key = String(result || 'not_comparable').trim().toLowerCase();
  return `control-evaluation-result-${key}`;
}
